import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Entity, formatNumber } from '@debbie/common/dist/index.js';
import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { isDebtorPMV } from '@debbie/common/dist/cosmo/synapses/experian/rki.js';
import { FoundationCode, getDebtorsOnCustomer, } from '@debbie/common/dist/customers/index.js';
import { canRegisterInRKI, getDateOfLatestWarning, RKIRegistries, } from '@debbie/common/dist/debt-register/index.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { getFoundations } from '@debbie/common/dist/documents/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import * as debtRegisterAPI from '@debbie/cortex/dist/api/debt-register.js';
import { selectCaseUpdates } from '@debbie/cortex/dist/case/selectors.js';
import { selectCustomerById } from '@debbie/cortex/dist/customers/selectors.js';
import { selectMe } from '@debbie/cortex/dist/me/selectors.js';
import { selectTemplates } from '@debbie/cortex/dist/templates/selectors.js';
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js';
import { Button, Modal, Well, Input, Checkbox, Select, DateInput, SearchInput, Table, } from '../index.js';
import rkiLogo from '../../assets/images/rki-partner-logo.png';
import THEME from '../../theme.js';
import Alert from '../Alert.js';
import { Row, Col } from '../Grid/index.js';
import { interactionsByReferenceKey, useInteractionsByReference, } from '@debbie/cortex/dist/interactions/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
const NewRegistrationModal = (props) => {
    const { debtorUser, setUserId, case: singleCase, caseVouchers: kaseVouchers, } = props;
    const lang = useSelector((s) => s.lang);
    const [amount, setAmount] = useState(null);
    const [withFoundation, setWithFoundation] = useState(false);
    const [foundationCode, setFoundationCode] = useState(null);
    const [foundationDate, setFoundationDate] = useState(null);
    const [dateOfLatestWarning, setDateOfLatestWarning] = useState(null);
    const [reporting, setReporting] = useState(false);
    const [personName, setPersonName] = useState(null);
    const [errorDescription, setErrorDescription] = useState(null);
    const [errorRequest, setErrorRequest] = useState(null);
    const me = useSelector(selectMe);
    const tenant = useSelector(selectTenant);
    const interactionsLoadable = useInteractionsByReference(Entity.Case, singleCase?.caseId);
    const interactions = h.hasData(interactionsLoadable)
        ? interactionsLoadable.data
        : null;
    const updates = useSelector((s) => singleCase && selectCaseUpdates(s, singleCase.caseId));
    const templates = useSelector((s) => selectTemplates(s));
    const documentsLoadable = useDocumentsByReference(singleCase.caseId);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const creditorLoadable = useCreditor(singleCase?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const customer = useSelector((s) => singleCase && selectCustomerById(s, singleCase.customerId));
    const collectorLoadable = useCollector((props.case &&
        creditor &&
        getCollectorOnCase(props.case.type, props.case.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId)) ||
        undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    useEffect(() => {
        if (amount)
            return;
        if (!kaseVouchers) {
            setAmount(0);
            return;
        }
        const balance = -helpers.overview.balance(kaseVouchers);
        setAmount(balance / 100);
    }, [kaseVouchers]);
    useEffect(() => {
        if (!interactions) {
            q.invalidate([interactionsByReferenceKey(Entity.Case, singleCase.caseId)]);
        }
    }, [interactions]);
    useEffect(() => {
        if (!updates) {
            c.cases.getUpdates(singleCase.caseId);
        }
    }, [updates]);
    useEffect(() => {
        if (!templates) {
            c.templates.getAll();
        }
    }, [templates]);
    useEffect(() => {
        if (documents) {
            const foundations = getFoundations(documents);
            if (foundations.length > 0) {
                setWithFoundation(true);
                setFoundationDate(moment(foundations[0].signedAt || foundations[0].date));
            }
        }
    }, [documents]);
    useEffect(() => {
        setErrorRequest(null);
    }, []);
    useEffect(() => {
        if (interactions && updates && templates) {
            const date = getDateOfLatestWarning(interactions, updates, templates);
            if (date) {
                setDateOfLatestWarning(moment(date));
            }
        }
    }, [interactions, updates, templates]);
    const getPersonName = (debtorUser) => {
        if (!personName &&
            debtorUser &&
            debtorUser.details.companyRelations &&
            debtorUser.details.companyRelations[0] &&
            debtorUser.details.companyRelations[0].name) {
            return debtorUser.details.companyRelations[0].name;
        }
        return personName;
    };
    const registerReport = async () => {
        try {
            setReporting(true);
            if (!debtorUser) {
                throw new Error('Ingen debitor valgt.');
            }
            if (!foundationDate && !dateOfLatestWarning) {
                throw new Error('No foundation date');
            }
            await debtRegisterAPI.rkiReport(debtorUser.userId, singleCase.caseId, {
                amount: parseInt(`${(amount || 0) * 100}`),
                foundationCode: withFoundation
                    ? foundationCode || undefined
                    : undefined,
                foundationDate: withFoundation
                    ? foundationDate?.format()
                    : dateOfLatestWarning?.format(),
            }, getPersonName(debtorUser) || undefined);
            await c.cases.getById(singleCase.id);
            await c.customers.getById(props.customer.customerId);
            props.toggleModal();
        }
        catch (error) {
            console.error(error);
            const err = error?.message ? JSON.parse(error.message) : error;
            if (err.description.indexOf('Experian') !== -1) {
                setErrorRequest(err.request);
                setErrorDescription(err.description);
                alert(err.description +
                    `\n\nTag fat på Experian, hvis der er spørgsmål til fejlen. Henvis til Error Id'et og vedhæft evt. et skærmprint af af denne side, så de kan se hvilke data der er sendt med.`);
            }
            else {
                alert(err);
            }
        }
        setReporting(false);
    };
    const debtors = customer && getDebtorsOnCustomer(customer);
    const searchDebtors = (q) => debtors
        ? debtors
            .filter((d) => d.details.name?.toLowerCase().indexOf(q.toLowerCase()) !== -1)
            .map((d) => ({
            heading: d.details.name || '-',
            returnData: d.userId,
            key: d.userId,
        }))
        : [];
    const reg = kaseVouchers &&
        debtorUser &&
        interactions &&
        documents &&
        updates &&
        templates &&
        tenant &&
        canRegisterInRKI(singleCase, [debtorUser], kaseVouchers, interactions, documents, updates, templates, withFoundation);
    const isPMV = isDebtorPMV(debtorUser?.details);
    // For logging if the case should be registered in the open or closed RKI debt registry
    console.log({ canRegisterInRKI: reg });
    return (React.createElement(Modal, { header: lang.RKI.newRegistration, onToggle: props.toggleModal },
        React.createElement(Row, null,
            React.createElement(Col, { width: "6" },
                debtorUser && interactions && documents && updates && me && reg ? (React.createElement(React.Fragment, null,
                    reg.errors.map((e) => (React.createElement(Alert, { key: e, size: "small", message: lang.RKI.autoRKI.errors[e] }))),
                    React.createElement("br", null))) : null,
                React.createElement(SearchInput, { onSelect: (_userId) => {
                        setUserId(_userId);
                    }, static: debtorUser && debtorUser.details.name, onRemoveStatic: () => setUserId(null), placeholder: lang.RKI.chooseDebtor, forceOpen: true, searchAll: searchDebtors }),
                debtorUser && isPMV && (React.createElement(Input, { placeholder: "Navn p\u00E5 person", value: getPersonName(debtorUser) || '', onChange: (e) => setPersonName(e.target.value) })),
                React.createElement(Input, { placeholder: lang.RKI.amount, type: "number", value: amount || '', onChange: (e) => setAmount(e.target.value) }),
                React.createElement(Checkbox, { placeholder: lang.RKI.withFoundation, checked: withFoundation, onChange: (e) => setWithFoundation(e.target.checked) }),
                withFoundation ? (React.createElement(React.Fragment, null,
                    React.createElement(Select, { value: foundationCode, onChange: (e) => setFoundationCode(e.target.value) },
                        React.createElement("option", { value: "" },
                            "- ",
                            lang.RKI.chooseFoundationType,
                            " -"),
                        Object.values(FoundationCode).map((e) => (React.createElement("option", { key: e, value: e },
                            "[",
                            e,
                            "] ",
                            lang.RKI.foundationCode[e])))),
                    React.createElement(DateInput, { placeholder: lang.RKI.dateFoundation, onChange: (d) => setFoundationDate(d), value: foundationDate || undefined, maxDate: moment().toDate(), allowNull: true }))) : (React.createElement(DateInput, { placeholder: lang.RKI.latestWarningDate, onChange: (d) => {
                        setDateOfLatestWarning(d);
                    }, maxDate: moment().subtract(15, 'days').toDate(), value: dateOfLatestWarning || undefined, allowNull: true })),
                errorRequest ? (React.createElement(React.Fragment, null,
                    React.createElement("br", null),
                    React.createElement(Alert, { style: { backgroundColor: THEME.DANGEROUS }, key: errorDescription, size: "small", message: errorDescription }),
                    React.createElement(Table, { zebra: true, style: { fontSize: 'smaller' } }, Object.entries(errorRequest).map(([key, value]) => (React.createElement("tr", { key: key },
                        React.createElement("td", null, key),
                        React.createElement("td", null, value))))),
                    React.createElement("br", null))) : (React.createElement(React.Fragment, null,
                    React.createElement("br", null),
                    React.createElement(Table, { zebra: true },
                        React.createElement("tr", null,
                            React.createElement("td", null, "Kreditor"),
                            React.createElement("td", null,
                                React.createElement("b", null, creditor?.name))),
                        React.createElement("tr", null,
                            React.createElement("td", null, "Experian klientnr."),
                            React.createElement("td", null,
                                React.createElement("b", null, creditor?.experianClientId))),
                        React.createElement("tr", null,
                            React.createElement("td", null, "Debitortype"),
                            React.createElement("td", null, isPMV ? (React.createElement(React.Fragment, null,
                                React.createElement("b", null, "S"),
                                " (Personligt ejet mindre virksomhed)")) : customer?.isCompany ? (React.createElement(React.Fragment, null,
                                React.createElement("b", null, "F"),
                                " (Virksomhed)")) : (React.createElement(React.Fragment, null,
                                React.createElement("b", null, "P"),
                                " (Person)")))),
                        React.createElement("tr", null,
                            React.createElement("td", null, "Referencenummer"),
                            React.createElement("td", null,
                                React.createElement("b", null,
                                    singleCase.id,
                                    "-",
                                    debtorUser?.id))),
                        customer?.isCompany && (React.createElement("tr", null,
                            React.createElement("td", null, "Virksomhedsnavn"),
                            React.createElement("td", null,
                                React.createElement("b", null, debtorUser?.details.name)))),
                        customer?.isCompany && (React.createElement("tr", null,
                            React.createElement("td", null, "CVR"),
                            React.createElement("td", null,
                                React.createElement("b", null, debtorUser?.details.cvr)))),
                        React.createElement("tr", null,
                            React.createElement("td", null, "Adresse"),
                            React.createElement("td", null,
                                React.createElement("b", null, debtorUser?.details.address?.address))),
                        React.createElement("tr", null,
                            React.createElement("td", null, "Postnr."),
                            React.createElement("td", null,
                                React.createElement("b", null, debtorUser?.details.address?.zipcode))),
                        React.createElement("tr", null,
                            React.createElement("td", null, "Beskyttet adresse"),
                            React.createElement("td", null,
                                React.createElement("b", null, debtorUser?.details.addressProtection ? 'Ja' : 'Nej')))),
                    React.createElement("br", null))),
                React.createElement(Button, { block: true, disabled: !(parseInt(`${(amount || 0) * 100}`) &&
                        props.userId &&
                        ((withFoundation && foundationCode && foundationDate) ||
                            (!withFoundation && dateOfLatestWarning))), loading: reporting, onClick: registerReport },
                    lang.RKI.infobox.registerButton,
                    ' ',
                    reg && reg.details.registry
                        ? `${lang.RKI.infobox[withFoundation ? RKIRegistries.OPEN : RKIRegistries.CLOSED]}`
                        : null)),
            React.createElement(Col, { width: "6" },
                React.createElement(Well, null,
                    React.createElement("img", { height: "50px", src: rkiLogo }),
                    debtorUser && (React.createElement("p", null,
                        collector && collector.name,
                        " ",
                        lang.RKI.infobox.herebyRegister,
                        ' ',
                        React.createElement("b", null, debtorUser.details.name),
                        " ",
                        lang.RKI.infobox.inExperian,
                        ' ',
                        withFoundation
                            ? `${lang.RKI.infobox.open}`
                            : `${lang.RKI.infobox.closed}`,
                        ' ',
                        lang.RKI.infobox.debtorNotPaid,
                        ' ',
                        React.createElement("b", null, formatNumber((amount || 0) * 100)),
                        ".",
                        lang.RKI.infobox.criteria)),
                    React.createElement("ul", null,
                        React.createElement("li", null, lang.RKI.infobox.min18years),
                        React.createElement("li", null, lang.RKI.infobox.min3messages),
                        withFoundation ? (React.createElement(React.Fragment, null,
                            React.createElement("li", null, lang.RKI.infobox.totalDebt),
                            React.createElement("li", null, lang.RKI.infobox.hasFoundation))) : (React.createElement(React.Fragment, null,
                            React.createElement("li", null, lang.RKI.infobox.debtorWarning),
                            React.createElement("li", null, lang.RKI.infobox.totalPrincipal),
                            React.createElement("li", null, lang.RKI.infobox.noDemurral),
                            React.createElement("li", null, lang.RKI.infobox.institutionsInfo)))),
                    React.createElement("p", { style: {
                            opacity: 0.7,
                            fontSize: '.7em',
                        } }, lang.RKI.infobox.finePrint.NB),
                    React.createElement("p", { style: {
                            opacity: 0.7,
                            fontSize: '.7em',
                        } }, lang.RKI.infobox.finePrint.letter),
                    React.createElement("p", { style: {
                            opacity: 0.7,
                            fontSize: '.7em',
                        } }, lang.RKI.infobox.finePrint.expiration),
                    React.createElement("p", { style: {
                            opacity: 0.7,
                            fontSize: '.7em',
                        } }, lang.RKI.infobox.finePrint.faultyData),
                    React.createElement("p", { style: {
                            opacity: 0.7,
                            fontSize: '.7em',
                        } }, lang.RKI.infobox.finePrint.newClient))))));
};
export default NewRegistrationModal;
//# sourceMappingURL=NewRegistrationModal.js.map